<template>
    <div
        v-show="!silent"
        class="uploader"
        :class="[component.is, { uploading: isUploading, disabled: isVisuallyDisabled, dragging, dropzone, button }, colour]"
        :style="{ borderRadius: `${_radius}px` }"
    >
        <slot name="target" v-bind="{ dragHandlers, dragging, radius: _radius, circle }">
            <label
                class="target"
                :for="`uploader-${_.uid}`"
                :class="[{ compact }, purpose]"
                v-on="dragHandlers"
            >
                <slot v-bind="{ dragging, radius: _radius }">
                    <div
                        class="target-inner"
                        :class="{ 'droppable text-primary': dragging || isUploading }"
                        :style="{ borderRadius: `${_radius}px` }"
                    >
                        <template v-if="isUploading">
                            <div class="progress primary" :style="{ transform: `translateX(-${100 - progress}%)` }">
                                <template v-if="progress < 100">
                                    <Icon
                                        v-if="component.is.sm"
                                        name="upload"
                                        :style="{ left: `${100 - progress}%` }"
                                    />
                                    <Txt
                                        v-else
                                        size="xs"
                                        heading
                                        :style="{ left: `${100 - progress}%` }"
                                    >
                                        {{ $trans('video.uploading', { percent: `${progress}%` }) }}
                                    </Txt>
                                </template>
                                <div v-else class="complete">
                                    <Icon
                                        name="correct-circle"
                                        class="text-primary"
                                        :size="compact ? '24' : '40'"
                                    />
                                    <Txt
                                        size="xs"
                                        heading
                                    >
                                        {{ $trans('video.uploading.complete') }}
                                    </Txt>
                                </div>
                            </div>
                            <Icon
                                v-if="component.is.sm"
                                name="upload"
                            />
                            <Txt
                                v-else
                                size="xs"
                                heading
                                class="text-primary"
                            >
                                {{ $trans('video.uploading', { percent: `${progress}%` }) }}
                            </Txt>
                        </template>
                        <div v-else>
                            <Icon
                                v-if="dragging || isUploading"
                                name="upload"
                                :size="compact ? '24' : '32'"
                                class="text-primary"
                            />
                            <Icon
                                v-else
                                :name="icon"
                                :size="compact ? '24' : '32'"
                                class="anchor"
                            />
                            <Expand v-if="component.width > 180 && !dragging">
                                <Txt
                                    v-if="placeholder"
                                    size="xxs" heading
                                    align="center"
                                >
                                    {{ placeholder }}
                                    <slot name="placeholder" />
                                </Txt>
                                <Txt
                                    v-else
                                    size="xxs" heading
                                    align="center"
                                >
                                    {{ $trans('upload.drag_file') }}
                                    <span class="text-primary">
                                        {{ $trans('upload.drag_file.browse') }}
                                    </span>
                                    <slot name="placeholder" />
                                </Txt>
                            </Expand>
                        </div>
                        <Border
                            v-if="!isUploading"
                            :animate="dragging"
                            :radius="_radius"
                            :circle="circle"
                            dashed
                        />
                    </div>
                    <Txt
                        v-if="error"
                        size="xxs"
                    >
                        <Icon name="danger" size="16" class="text-danger" />
                        {{ error }}
                    </Txt>
                </slot>
                <input
                    v-if="!disabled"
                    :id="`uploader-${_.uid}`"
                    ref="input"
                    type="file"
                    :multiple="multiple"
                    :accept="accept"
                    :class="{
                        under: isUploading || (dragOnly && !dragging),
                        'visually-hidden': button
                    }"
                    @change="onFileChange($event.target.files)"
                    @click="onClick"
                />
            </label>
        </slot>
    </div>
</template>

<script>
import axios from 'axios';
const CancelToken = axios.CancelToken;

import { useResponsive, getElement } from '@/store/responsive';

import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Border from '@/components/utility/border';
import Expand from '@/components/animation/expand';

const FILE_SIZE_LIMIT = 2500 * 1024 * 1024; // 2.5GB

export default {
    name: 'Uploader',
    components: { Txt, Icon, Border, Expand },
    inject: ['$global'],
    props: {
        value: { type: [Object, File, Array, String], default: null },
        accept: { type: String, default: null },
        purpose: { type: String, default: null },
        placeholder: { type: String, default: null },
        radius: { type: Number, default: undefined },
        disabled: { type: Boolean, default: false },

        dragOnly: { type: Boolean, default: false },
        compact: { type: Boolean, default: false },
        dropzone: { type: Boolean, default: false },
        circle: { type: Boolean, default: false },
        silent: { type: Boolean, default: false },
        preventClick: { type: Boolean, default: true },
        reset: { type: Boolean, default: true },
        multiple: { type: Boolean, default: false },
        button: { type: Boolean, default: false },

        colour: { type: String, default: null, options: ['translucent', 'translucent-dark'] }
    },
    emits: ['dragging', 'uploading', 'change', 'drop', 'update:value', 'error:filetype', 'resize'],
    data() {
        return {
            error: null,
            uploading: null,
            dragging: false
        };
    },
    computed: {
        component() {
            return getElement(this);
        },
        dragHandlers() {
            return {
                dragenter: this.startDragging,
                dragend: this.stopDragging,
                dragleave: this.stopDragging,
                drop: event => {
                    event.stopPropagation();
                    this.stopDragging();
                }
            };
        },
        breakpoints() {
            return {
                sm: el => el.width < 199,
                md: el => el.width >= 200 && el.width < 299,
                lg: el => el.width >= 300 && el.width < 599,
                xl: el => el.width >= 600
            };
        },
        _radius() {
            if (this.radius !== undefined) return this.radius;
            if (this.compact) return 16;
            if (this.component.is.xl) return 32;
            if (this.component.is.lg) return 24;
            if (this.component.is.md) return 16;
            if (this.component.is.sm) return 12;
            return 16;
        },
        isVisuallyDisabled() {
            if (this.colour !== null) {
                return false;
            }
            return this.disabled;
        },
        acceptArray() {
            if (!this.accept) return;
            return this.accept.split(',');
        },
        icon() {
            return {
                image: 'add-image',
                video: 'video-add'
            }[this.purpose] || 'upload';
        },
        progress() {
            return this.uploading && this.uploading.progress;
        },
        isUploading() {
            return !!this.uploading;
        }
    },
    watch: {
        dragging(to) {
            this.$emit('dragging', to);
        },
        progress() {
            this.$emit('uploading', this.isUploading);
        },
        component: {
            handler(to) {
                this.$emit('resize', { width: to.width, height: to.height });
            },
            deep: true,
            immediate: true
        },
        value: {
            handler(to) {
                if (to && to instanceof File) {
                    this.onFileChange([to]);
                } else if (!to) {
                    if (this.$refs.input) this.$refs.input.value = '';
                }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {
        useResponsive(this, this.$el, this.breakpoints);
    },
    beforeUnmount() {
        useResponsive.removeElement(this);
    },
    methods: {
        startDragging() {
            this.dragging = true;
        },
        stopDragging() {
            this.dragging = false;
        },
        setError(type) {
            this.error = this.$trans(type);
        },
        onClick(event) {
            if (this.dropzone && this.preventClick) event.preventDefault();
        },
        onFileChange(files) {
            this.error = null;
            this.stopDragging();
            this.uploading = null;
            if (!files.length) return;

            this.$emit('change', files);
            const _files = Array.from(files);

            _files.forEach(file => {
                if (!this.isAllowedType(file.type)) {
                    this.setError('upload.file_type_error');
                    this.$emit('error:filetype', file);
                    file.error = true;
                }
            });
            _files.forEach(file => {
                if (file.size && file.size > FILE_SIZE_LIMIT) {
                    this.setError(`This file exceeds the file size limit of ${FILE_SIZE_LIMIT / 1024 / 1024}MB. Please select another file.`);
                    this.$emit('error:filetype', file);
                    file.error = true;
                }
            });

            if (_files.some(file => file.error)) return;
            
            if (this.dropzone) {
                const file = this.multiple ? _files : _files[0];
                this.$emit('drop', file);
                this.$emit('update:value', file);
                return;
            }

            _files.forEach(file => this.upload(file));
        },
        upload(file) {
            this.uploading = { progress: 0 };
            const config = {
                cancelToken: new CancelToken(cancel => this.uploading.cancel = cancel),
                onUploadProgress: e => {
                    if (!this.uploading) return;
                    this.uploading.progress = Math.round((e.loaded * 100) / e.total);
                },
                withCredentials: false,
                headers: {
                    'Content-Type': this.getType(file),
                    Authorization: undefined
                },
                transformRequest: [(data) => data]
            };

            let path, hash;
            return this.getPresignedUrl(file)
                .then(({ data }) => {
                    path = data.path;
                    hash = data.hash;
                    return this.$http.put(data.url, file, config);
                })
                .then(() => {
                    if (this.reset) this.uploading = null;
                    const response = { path, hash, type: this.getType(file), name: file.name, size: file.size };
                    this.$emit('update:value', response);
                    return response;
                })
                .catch(error => {
                    this.setError(error.response && error.response.data ? error.response.data.message : error.message);
                    return Promise.reject(error);
                });
        },
        getPresignedUrl(file) {
            const params = {
                purpose: this.purpose,
                client: this.$global && this.$global.client ? this.$global.client.id : this.$window.client.id,
                type: this.getType(file),
                name: file.name,
                size: file.size
            };
            return this.$http.post('/upload', params);
        },
        getType(file) {
            if (file.type) return file.type;
            const extension = file.name.split('.').pop();
            return {
                sb3: 'application/zip'
            }[extension] || 'application/octet-stream';
        },
        cancelUpload(upload) {
            if (upload.cancel) upload.cancel();
            this.uploading = null;
        },
        resetUpload() {
            this.error = null;
            this.uploading = null;
            this.$emit('update:value', null);
        },
        isAllowedType(type) {
            if (!type) return true;
            if (!(this.acceptArray && this.acceptArray.length)) return true;
            if (this.acceptArray.includes(type)) return true;

            return this.acceptArray.some(accept => {
                const [acceptType, acceptSubtype] = accept.split('/');
                const typeSplit = type.split('/');
                return acceptSubtype === '*' && typeSplit[0] === acceptType;
            });
        }
    }
};
</script>

<style lang="less" scoped>
.uploader {
    --heading: 'Poppins';
    display: flex;
    background-color: var(--smoke);
    &.full-height {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        height: 100%;
        .target {
            .image, .video { height: 100% }
        }
    }
    &.xl .target { padding: 32px; }
    &.lg .target { padding: 24px; }
    &.md .target { padding: 16px; }
    &.sm .target { padding: 12px; }
    &.button .target { padding: 0; }
    &.uploading .target {
        padding: 0;
        &.compact { padding: 0; }
        .target-inner .border { color: var(--primary); }
    }
    &.disabled:not(.translucent):not(.translucent-dark) {
        background-color: var(--cloud);
        color: var(--steel);
        .target {
            &:hover {
                .target-inner {
                    span.icon {
                        color: var(--steel);
                        fill: var(--steel);
                    }
                    .border { color: var(--steel); }
                }
            }
            .target-inner {
                .txt-component.heading.xxs { color: var(--steel); }
                span.icon {
                    color: var(--steel);
                    fill: var(--steel);
                }
                .border { color: var(--steel); }
            }
            .text-primary { opacity: 0.25; }
            input { cursor: auto; }
        }
    }

    .target {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        position: relative;
        height: 100%;
        margin: 0;
        float: none;
        &.compact {
            min-height: 80px;
            padding: 8px;
            .target-inner {
                padding: 0;
                min-height: auto;
                > * {
                    flex-direction: row;
                    .txt-component.heading.xxs {
                        margin-top: 0;
                        margin-left: 8px;
                    }
                }
                .progress {
                    .complete {
                        flex-direction: row;
                        .txt-component.heading.xs, .icon {
                            width: auto;
                            margin: 0;
                        }
                        .icon { margin-right: 8px; }
                    }
                }
            }
        }
        &:hover {
            .target-inner {
                color: var(--primary);
                span.icon {
                    color: var(--primary);
                    fill: var(--primary);
                }
                .border { color: var(--primary); }
            }
        }
        .target-inner {
            position: relative;
            margin: 0;
            flex: 1 1 auto;
            display: flex;
            justify-content: center;
            padding: 24px 16px;
            user-select: none;
            border-radius: inherit;
            -webkit-mask-image: -webkit-radial-gradient(white, black);
            &.droppable { color: var(--primary); }
            .border {
                position: static;
                color: var(--steel);
            }
            p.sm {
                display: flex;
                align-items: center;
            }
            .txt-component.heading.xxs {
                color: var(--coal);
                margin-top: 16px; 
            }
            .progress, .cancel {
                transition: all 0.35s linear;
                position: absolute;
                height: 100%;
                top: 0;
                left: 0;
                z-index: 1;
                overflow: hidden;
            }
            .cancel { z-index: 2; }
            .progress {
                width: 100%;
                .txt-component.heading.xs, .icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.35s linear;
                    position: absolute;
                    left: 0;
                    width: 100%;
                    color: #FFF;
                    margin: 0;
                }
                .complete {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    width: 100%;
                    height: 100%;
                    .txt-component.heading.xs { position: static; }
                    .icon {
                        fill: #FFF;
                        border-radius: 100%;
                        margin-bottom: 12px;
                        position: static;
                    }
                }
            }
            > * {
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                .txt-component.heading { margin-top: 8px; }
            }
        }
        input {
            position: absolute;
            cursor: pointer;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
            &.under { z-index: -1; }
        }
    }
    .uploaded, .uploading {
        padding: 16px 24px;
        margin: 8px 0;
        background-color: var(--smoke);
    }
    .file {
        display: flex;
        margin-bottom: 8px;
        width: 100%;
        &:last-child { margin: 0; }
        .file-icon {
            display: flex;
            align-items: center;
            justify-content: center;

            background-size: cover;
            background-color: var(--cloud);
            width: 40px;
            height: 40px;
            border-radius: 4px;
            margin-right: 16px;
        }
        .detail {
            flex: 1 1 auto;
            .title {
                display: flex;
                justify-content: space-between;
                margin-top: 4px;
                p {
                    max-width: 200px;
                    display: block;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    margin: 0;
                }
                .cobutton { padding: 0; }
                .cobutton, span { margin-left: 16px; }
            }
        }
        p.error { font-size: 11px; }
        .progress { margin-top: 8px; }
    }
    &.translucent-dark, &.translucent {
        .target {
            .target-inner {
                color: rgba(255, 255, 255, 0.5);
                span.icon {
                    color: rgba(255, 255, 255, 0.5);
                    fill: rgba(255, 255, 255, 0.5);
                }
                .border { color: rgba(255, 255, 255, 0.5); }
                .txt-component.heading.xxs { color: #FFF; }
            }
            &:hover {
                .target-inner {
                    color: #FFF;
                    span.icon {
                        color: #FFF;
                        fill: #FFF;
                    }
                    .border { color: #FFF }
                }
            }
        }
    }
    &.translucent-dark {
        background-color: ~"rgba(var(--coal-rgb), 0.25)";
    }
    &.translucent{
        background-color: rgba(255, 255, 255, 0.12);
    }
}
</style>

<style lang="less">
.uploader .target {
    .txt-component.xxs.body {
        position: absolute;
        top: 100%;
        .txt-component-content {
            height: 24px;
            display: flex;
            align-items: center;
            .icon { margin-right: 4px; }
        }
    }
    .target-inner {
        min-height: 240px;
        > * { flex-direction: column; }
    }
    .primary {
        background-color: var(--button-colour, var(--primary));
        color: var(--button-colour-contrast, var(--primary));
    }
    .text-primary {
        color: var(--button-colour, var(--primary));
    }
}
</style>
